import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Form, Modal, Alert } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { apiFetch } from '../api';
import { PageHeader } from './ui';
import { formatDate } from '../utils/date';
import { useAuth } from '../auth/AuthContext';
import { canManageRemnants } from '../auth/roles';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const UNITS = [
  { value: 'шт', label: 'шт.' },
  { value: 'кг', label: 'кг' },
  { value: 'м', label: 'м' },
  { value: 'м2', label: 'м²' },
  { value: 'компл', label: 'компл.' },
];

function RemnantsPage() {
  const [remnants, setRemnants] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // null – создание, иначе объект остатка
  const [form, setForm] = useState({
    contract: '',
    name: '',
    quantity: '',
    unit: 'шт',
    location: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { user } = useAuth();
  const canEdit = canManageRemnants(user?.role);

  const loadRemnants = useCallback(() => {
    setLoading(true);
    apiFetch('/api/remnants/?page_size=100')
      .then((data) => {
        setRemnants(data.results || data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Не удалось загрузить архив остатков');
        setLoading(false);
      });
  }, []);

  const loadContracts = useCallback(() => {
    apiFetch('/api/contracts/?page_size=100')
      .then((data) => {
        setContracts(data.results || data);
      })
      .catch((err) => console.error('Ошибка загрузки договоров:', err));
  }, []);

  useEffect(() => {
    loadRemnants();
    loadContracts();
  }, [loadRemnants, loadContracts]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm({ contract: '', name: '', quantity: '', unit: 'шт', location: '', notes: '' });
    setFormError('');
    setShowModal(true);
  };

  const handleOpenEdit = (remnant) => {
    setEditing(remnant);
    setForm({
      contract: remnant.contract || '',
      name: remnant.name,
      quantity: remnant.quantity,
      unit: remnant.unit,
      location: remnant.location || '',
      notes: remnant.notes || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.contract) {
      setFormError('Выберите договор');
      return;
    }
    if (!form.name.trim()) {
      setFormError('Введите наименование');
      return;
    }
    if (!form.quantity) {
      setFormError('Введите количество');
      return;
    }
    setSaving(true);
    const payload = {
      contract: parseInt(form.contract, 10),
      name: form.name.trim(),
      quantity: parseFloat(form.quantity),
      unit: form.unit,
      location: form.location.trim(),
      notes: form.notes.trim(),
    };
    try {
      if (editing) {
        await apiFetch(`/api/remnants/${editing.id}/`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch('/api/remnants/', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      setShowModal(false);
      loadRemnants();
    } catch (err) {
      setFormError(err.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    await apiFetch(`/api/remnants/${deleteTarget.id}/`, { method: 'DELETE' });
    setDeleteTarget(null);
    loadRemnants();
  };

  if (loading) return <div className="empty-state">Загрузка...</div>;
  if (error) return <div className="empty-state text-danger">{error}</div>;

  return (
    <>
      <PageHeader
        title="📦 Архив остатков"
        subtitle="Управление остатками по договорам"
        actions={
          canEdit && (
            <button type="button" className="app-btn app-btn-primary" onClick={handleOpenCreate}>
              <FaPlus />
            </button>
          )
        }
      />

      {remnants.length === 0 ? (
        <div className="empty-state">Нет записей об остатках</div>
      ) : (
        <div className="remnants-list">
          {remnants.map((remnant) => (
            <div key={remnant.id} className="remnant-card">
              <div className="remnant-card-body">
                <div className="remnant-card-header">
                  <span className="remnant-name">{remnant.name}</span>
                  <Badge bg="secondary">{remnant.unit}</Badge>
                </div>
                <div className="remnant-details">
                  <span>Количество: <strong>{remnant.quantity}</strong></span>
                  <span>Место: {remnant.location || '—'}</span>
                  <span>Договор: {remnant.contract || '—'}</span>
                  <span>Обновлён: {formatDate(remnant.updated_at)}</span>
                </div>
                {remnant.notes && <div className="remnant-notes">{remnant.notes}</div>}
                {canEdit && (
                  <div className="remnant-actions">
                    <button
                      type="button"
                      className="app-btn app-btn-sm"
                      onClick={() => handleOpenEdit(remnant)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="app-btn app-btn-sm app-btn-danger-outline"
                      onClick={() => setDeleteTarget(remnant)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Модальное окно создания/редактирования */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton className="app-surface">
          <Modal.Title>{editing ? 'Редактировать остаток' : 'Добавить остаток'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body className="app-surface">
            {formError && <Alert variant="danger">{formError}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label>Договор</Form.Label>
              <Form.Select
                value={form.contract}
                onChange={(e) => setForm({ ...form, contract: e.target.value })}
                required
              >
                <option value="">Выберите договор</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.number} — {c.product_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Наименование</Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                placeholder="Наименование остатка"
              />
            </Form.Group>
            <div className="row g-2">
              <div className="col-6">
                <Form.Group>
                  <Form.Label>Количество</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    required
                    placeholder="0"
                  />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group>
                  <Form.Label>Ед. изм.</Form.Label>
                  <Form.Select
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                  >
                    {UNITS.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mt-3">
              <Form.Label>Место хранения</Form.Label>
              <Form.Control
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Склад, стеллаж, участок"
              />
            </Form.Group>
            <Form.Group className="mt-3">
              <Form.Label>Примечание</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Дополнительная информация"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="app-surface">
            <button type="button" className="app-btn" onClick={() => setShowModal(false)}>
              Отмена
            </button>
            <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ConfirmDeleteModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Удалить остаток?"
        message="Запись будет удалена из архива остатков."
        itemName={deleteTarget?.name}
      />
    </>
  );
}

export default RemnantsPage;